/** Lux & billing › Plan & usage: the subscription's state, "Manage billing" (Stripe's Customer Portal: change
 * plan, cancel, payment methods, invoices) and extra users. Subscribing is the upgrade section (Upgrade.tsx).
 * Extra users renew with the plan (monthly or yearly; annual ones at the plan's discount): added ones are charged
 * today for the rest of the period; removed ones stay until the renewal (no credit), shown as a waiting reduction
 * that can be undone. A Free company buys them too: the first ones through Stripe Checkout (monthly or annual).
 * Extra users can also be paid with purchased Lux (LuxSeats below): a month or a year from today at their cash
 * price, valued at what the Lux cost; both kinds count together. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLuxFormat, useLuxSpend } from "../components/LuxSpend";
import { useLeaving, useOnce } from "../components/useOnce";
import { activityHref, PurchaseIntent } from "../lib/luxSpend";
import { AlertTriangle, CalendarClock, ExternalLink, Undo2, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { DirIcon } from "../i18n/mirror";
import { api, ApiError, errorMessage, unwrap, type S } from "../api/client";
import { Alert, Badge, Button, Modal, NumberInput, Segmented, Spinner, Switch, useToast } from "../components/ui";
import { LuxGlyph } from "../components/LuxIcon";
import { seatQuoteText } from "../lib/lux";
import {
  INTENT_KEY,
  paidSeats,
  pendingSeatsText,
  seatChangeText,
  seatPriceText,
  subscriptionLine,
  wantedSeats,
  type Billing,
} from "../lib/billing";

export function useBilling(orgId: string, admin = false, poll = false) {
  return useQuery({
    queryKey: ["billing", orgId, admin],
    refetchInterval: poll ? 2500 : false,
    queryFn: () =>
      admin
        ? unwrap(api.GET("/v1/admin/orgs/{org_id}/billing", { params: { path: { org_id: orgId } } }))
        : unwrap(api.GET("/v1/orgs/{org_id}/billing", { params: { path: { org_id: orgId } } })),
  });
}

export function BillingPanel({ orgId, canBuy }: { orgId: string; canBuy: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const f = useLuxFormat();
  const { t } = f;
  const date = (iso: string) => f.date(iso);
  const [params, setParams] = useSearchParams();
  const status = params.get("billing");
  // back from a paid Checkout: the plan (or the extra users) arrives with Stripe's webhook, usually in seconds. Until
  // it has, the page asks again (for two minutes at most) and then shows it, with the plan's Lux and limits.
  const [since] = useState(() => Date.now());
  const [arrived, setArrived] = useState(false);
  const confirming = (status === "success" || status === "seats") && !arrived && Date.now() - since < 120_000;
  const billing = useBilling(orgId, false, confirming);
  useEffect(() => {
    const d = billing.data;
    if (!confirming || !d) return;
    const live = (x: { status: string } | null | undefined) => !!x && ["active", "trialing"].includes(x.status);
    if (status === "success" ? live(d.subscription) : paidSeats(d.seats_subscription) > 0) {
      setArrived(true);
      qc.invalidateQueries({ queryKey: ["org-plan", orgId] });
      qc.invalidateQueries({ queryKey: ["lux", orgId] });
    }
  }, [billing.data, confirming, status, orgId, qc]);
  const [seats, setSeats] = useState<number | null>(null);
  const [freePeriod, setFreePeriod] = useState<Billing>("annual");
  useEffect(() => {
    try {
      sessionStorage.removeItem(INTENT_KEY); // the landing page's handoff has arrived
    } catch {
      /* private mode */
    }
  }, []);
  useEffect(() => {
    if (billing.data) setSeats(wantedSeats(billing.data.seats_subscription));
  }, [billing.data]);
  // each button acts once however often it is clicked (components/useOnce.ts)
  const portal = useLeaving({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/billing/portal", { params: { path: { org_id: orgId } } })),
    url: (r) => r.url,
    onError: (e) => toast(errorMessage(e), true),
  });
  const seatsCheckout = useLeaving({
    mutationFn: (body: { extra_seats: number; billing: Billing }) =>
      unwrap(api.POST("/v1/orgs/{org_id}/billing/seats/checkout", { params: { path: { org_id: orgId } }, body })),
    url: (r) => r.url,
    onError: (e) => toast(errorMessage(e), true),
  });
  const saveSeats = useOnce({
    mutationFn: (extra: number) =>
      unwrap(api.PUT("/v1/orgs/{org_id}/billing/seats", { params: { path: { org_id: orgId } }, body: { extra_seats: extra } })),
    onSuccess: (data, extra) => {
      const before = paidSeats(billing.data?.seats_subscription);
      const undone = extra === before && !!pendingSeatsText(billing.data?.seats_subscription, date, f);
      qc.setQueryData(["billing", orgId, false], data);
      qc.invalidateQueries({ queryKey: ["org-plan", orgId] });
      const waiting = pendingSeatsText(data.seats_subscription, date, f);
      toast(extra > before ? t("lux.seats.added") : undone ? t("lux.seats.undone", { count: before }) : (waiting ?? t("lux.seats.updated")));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!billing.data) return billing.isLoading ? <Spinner /> : null;
  const b = billing.data;
  const sub = b.subscription?.status && b.subscription.status !== "canceled" ? b.subscription : null;
  const subscribed = !!sub && ["active", "trialing", "past_due"].includes(sub.status);
  const seatSub = b.seats_subscription;
  const paid = paidSeats(seatSub);
  const live = paid > 0 ? seatSub : null;
  const onFree = b.plan === "free" && !b.comped;
  // Extra users follow the company's plan; their interval is the plan's, or chosen at checkout on Free.
  const seatPlan = live ? live.plan : subscribed && sub ? sub.plan : onFree ? "free" : null;
  const seatBilling: Billing = live ? live.billing : subscribed && sub ? sub.billing : freePeriod;
  const seatPriceOf = (period: Billing) => b.seat_prices.find((x) => x.plan === seatPlan && x.billing === period);
  const seatPrice = seatPriceOf(seatBilling)?.amount_cents ?? null;
  const discount = seatPriceOf("annual")?.annual_discount_pct ?? 0;
  const buying = onFree && !live; // the first extra users on Free go through Checkout
  const asked = wantedSeats(seatSub);
  const wanted = Math.max(0, Math.round(seats ?? asked));
  const waiting = pendingSeatsText(seatSub, date, f);
  const money = (c: number) => f.moneyShort(c);
  // seats renew with the plan: today's price uses the seats' period, or the plan's for the first ones
  const period = live ?? (subscribed ? sub : null);
  const change =
    seatPrice && !buying
      ? seatChangeText({ paid, wanted, current: asked, amountCents: seatPrice, billing: seatBilling, periodStart: period?.current_period_start,
          periodEnd: period?.current_period_end, money, formatDate: (iso) => date(iso), words: f })
      : null;
  const buyCount = Math.max(1, wanted);
  const priceText = seatPrice ? seatPriceText(seatPrice, seatBilling, money, discount, subscribed, f) : "";
  const priceLine = subscribed ? t("lux.seats.withPlan", { price: priceText }) : priceText;
  const dismiss = () => {
    params.delete("billing");
    params.delete("upgrade");
    setParams(params, { replace: true });
  };
  return (
    <div className="billing-panel">
      {status === "seats" && (
        <Alert tone="ok" title={t("lux.seats.thanksTitle")} action={<Button size="sm" variant="ghost" onClick={dismiss}>{t("lux.dismiss")}</Button>}>
          {t("lux.seats.thanksBody")}
        </Alert>
      )}
      {status === "success" && (
        <Alert tone="ok" title={t("lux.billing.thanksTitle")} action={<Button size="sm" variant="ghost" onClick={dismiss}>{t("lux.dismiss")}</Button>}>
          {t("lux.billing.thanksBody")}
        </Alert>
      )}
      {status === "canceled" && (
        <Alert tone="warn" title={t("lux.checkout.cancelledTitle")} action={<Button size="sm" variant="ghost" onClick={dismiss}>{t("lux.dismiss")}</Button>}>
          {t("lux.checkout.cancelledBody")}
        </Alert>
      )}
      {sub?.status === "past_due" && (
        <Alert tone="warn" title={t("lux.billing.failedTitle")}>
          <AlertTriangle size={14} style={{ verticalAlign: -2 }} />{" "}
          {/* past the grace the company is on Free already: never "before <a date gone by>, or back to Free" */}
          {b.plan === "free" && !b.comped
            ? t("lux.billing.failedFreeBody")
            : t("lux.billing.failedBody", { date: b.grace_ends_at ? date(b.grace_ends_at) : "none" })}
        </Alert>
      )}
      {b.comped && <Alert tone="info">{t("lux.billing.comped")}</Alert>}
      {subscribed && sub ? (
        <div className="billing-row">
          <div>
            <b>{t("lux.billing.planLine", { plan: sub.plan === "pro" ? "Pro" : "Business", billing: sub.billing })}</b>{" "}
            <Badge tone={sub.status === "past_due" ? "warn" : "ok"}>{t("lux.billing.status", { status: sub.status })}</Badge>
            {sub.coupon_code && <Badge tone="info" plain>{sub.coupon_code}</Badge>}
            <div className="muted small">{subscriptionLine(sub, date, f)}</div>
          </div>
          <Button icon={<DirIcon><ExternalLink /></DirIcon>} disabled={!canBuy} loading={portal.busy} onClick={() => portal.run(undefined)}>
            {t("lux.billing.manage")}
          </Button>
        </div>
      ) : null}
      {buying && seatPrice ? (
        <div className="billing-row seats-row">
          <div className="seats-head">
            <div>
              <b>
                <Users size={14} style={{ verticalAlign: -2 }} /> {t("lux.seats.title")}
              </b>
              <div className="muted small">{t("lux.seats.freeIntro", { price: priceLine })}</div>
            </div>
            <Segmented<Billing>
              value={freePeriod}
              onChange={setFreePeriod}
              options={[
                { value: "monthly", label: t("lux.billing.monthly") },
                { value: "annual", label: discount ? t("lux.billing.annualOff", { off: f.pct(discount) }) : t("lux.billing.annual") },
              ]}
            />
          </div>
          <div className="seats-head">
            <div className="seats-note is-charge" role="status">
              {t("lux.seats.buyNote", {
                count: buyCount,
                price: money(seatPrice * buyCount),
                per: seatBilling === "annual" ? "year" : "month",
                monthly: seatBilling === "annual" && seatPriceOf("monthly") ? money(seatPriceOf("monthly")!.amount_cents * 12 * buyCount) : "none",
              })}
            </div>
            <div className="row seats-control">
              <div style={{ width: 90 }}>
                <NumberInput value={buyCount} min={1} max={500} step={1} onChange={setSeats} />
              </div>
              <Button variant="primary" disabled={!canBuy || !b.available} loading={seatsCheckout.busy} onClick={() => seatsCheckout.run({ extra_seats: buyCount, billing: freePeriod })}>
                {t("lux.seats.buyStripe")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {!buying && seatPlan && seatPrice ? (
        <div className="billing-row seats-row">
          <div className="seats-head">
            <div>
              <b>
                <Users size={14} style={{ verticalAlign: -2 }} /> {t("lux.seats.title")}
              </b>
              {paid > 0 && (
                <Badge tone="info" plain>
                  {t("lux.seats.paidBadge", { count: paid })}
                </Badge>
              )}
              <div className="muted small">{t("lux.seats.intro", { price: priceLine })}</div>
            </div>
            <div className="row seats-control">
              <div style={{ width: 90 }}>
                <NumberInput value={seats} min={0} max={500} step={1} onChange={setSeats} />
              </div>
              <Button disabled={!canBuy || seats == null || wanted === asked || saveSeats.busy} loading={saveSeats.busy && saveSeats.variables !== paid} onClick={() => saveSeats.run(wanted)}>
                {wanted > paid ? t("lux.seats.addPay") : t("lux.seats.update")}
              </Button>
            </div>
          </div>
          {change && (
            <div className={wanted > paid ? "seats-note is-charge" : "seats-note"} role="status">
              {change}
            </div>
          )}
          {waiting && (
            <div className="seats-pending">
              <CalendarClock size={16} />
              <span>{waiting}</span>
              <Button size="sm" variant="ghost" icon={<Undo2 />} disabled={!canBuy || saveSeats.busy} loading={saveSeats.busy && saveSeats.variables === paid} onClick={() => saveSeats.run(paid)}>
                {t("lux.seats.keep", { count: paid })}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {/* a plan with users by contract (Enterprise) sells none, for money or Lux */}
      {b.seat_prices.some((x) => x.plan === b.plan) && <LuxSeats orgId={orgId} canBuy={canBuy} seats={b.lux_seats ?? []} />}
    </div>
  );
}

type Term = "month" | "year";

/** Extra users paid with purchased Lux: a prepaid month or year from today, non-refundable. Their price is the cash
 * seat price, paid by purchased Lux at what it cost; the quote shows the Lux before confirming. */
export function LuxSeats({ orgId, canBuy, seats }: { orgId: string; canBuy: boolean; seats: readonly S["LuxSeat"][] }) {
  const qc = useQueryClient();
  const lux = useLuxSpend();
  const toast = useToast();
  const f = useLuxFormat();
  const { t } = f;
  const [term, setTerm] = useState<Term>("month");
  const [quantity, setQuantity] = useState<number | null>(1);
  const [autoRenew, setAutoRenew] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // one Idempotency-Key per purchase: made when the confirmation opens, reused by its retries (bought once)
  const intent = useRef(new PurchaseIntent());
  const n = Math.max(1, Math.min(100, Math.round(quantity ?? 1)));
  const quote = useQuery({
    queryKey: ["lux-seat-quote", orgId, n, term],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/lux/seats/quote", { params: { path: { org_id: orgId }, query: { quantity: n, term } } })),
    placeholderData: (prev) => prev,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["billing", orgId] });
    qc.invalidateQueries({ queryKey: ["org-plan", orgId] });
    qc.invalidateQueries({ queryKey: ["lux", orgId] });
    qc.invalidateQueries({ queryKey: ["lux-activity", orgId] });
    qc.invalidateQueries({ queryKey: ["lux-seat-quote", orgId] });
  };
  const buy = useOnce({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/lux/seats", {
          params: { path: { org_id: orgId }, header: { "Idempotency-Key": intent.current.key() } },
          body: { quantity: n, term, auto_renew: autoRenew, expected_lux: quote.data?.lux ?? 0 },
        }),
      ),
    onSuccess: (seat) => {
      setConfirming(false);
      lux.spent({ amount: seat.lux, label: t("lux.seats.until", { count: seat.quantity, date: f.date(seat.ends_at) }), href: activityHref("seat") });
      refresh();
    },
    onError: (e) => {
      toast(errorMessage(e), true);
      // this confirmation's key already bought extra users (with other choices): show them
      if (e instanceof ApiError && e.problem.code === "idempotency_key_reused") {
        setConfirming(false);
        refresh();
      }
      void quote.refetch();
    },
  });
  const openConfirmation = () => {
    intent.current.next();
    setConfirming(true);
    // another tab may have bought meanwhile: the seat list and the quote are read again behind the confirmation
    qc.invalidateQueries({ queryKey: ["billing", orgId] });
    void quote.refetch();
  };
  const renew = useMutation({
    mutationFn: (v: { id: string; auto_renew: boolean }) =>
      unwrap(api.PATCH("/v1/orgs/{org_id}/lux/seats/{seat_id}", { params: { path: { org_id: orgId, seat_id: v.id } }, body: { auto_renew: v.auto_renew } })),
    onSuccess: () => refresh(),
    onError: (e) => toast(errorMessage(e), true),
  });
  const q = quote.data;
  const live = seats.filter((x) => x.status === "active");
  return (
    <div className="billing-row seats-row lux-seats">
      <div className="seats-head">
        <div>
          <b>
            <LuxGlyph size={14} style={{ verticalAlign: -2 }} /> {t("lux.luxSeats.title")}
          </b>
          <div className="muted small">{t("lux.luxSeats.intro")}</div>
        </div>
        <Segmented<Term>
          value={term}
          onChange={setTerm}
          options={[
            { value: "month", label: t("lux.luxSeats.month") },
            { value: "year", label: t("lux.luxSeats.year") },
          ]}
        />
      </div>
      <div className="seats-head">
        <div className={q?.affordable ? "seats-note is-charge" : "seats-note"} role="status">
          {q ? (
            <>
              <FormattedMessage id="lux.luxSeats.quoteLine" values={{ b: (c) => <b>{c}</b>, count: n, date: f.date(q.ends_at), quote: seatQuoteText(q, f) }} />
              {!q.affordable && <span> · {t("lux.luxSeats.worth", { price: f.money(q.purchased_value_minor, q.currency) })}</span>}
            </>
          ) : quote.error ? (
            errorMessage(quote.error)
          ) : (
            "…"
          )}
        </div>
        <div className="row seats-control">
          <div style={{ width: 90 }}>
            <NumberInput value={quantity} min={1} max={100} step={1} onChange={setQuantity} />
          </div>
          <Button variant="primary" disabled={!canBuy || !q?.affordable} onClick={openConfirmation}>
            {q?.affordable ? t("lux.luxSeats.pay", { lux: f.lux(q.lux) }) : t("lux.luxSeats.payWith")}
          </Button>
        </div>
      </div>
      {live.length > 0 && (
        <ul className="lux-seat-list">
          {live.map((x) => (
            <li key={x.id}>
              <span>
                <FormattedMessage
                  id="lux.luxSeats.row"
                  values={{ b: (c) => <b>{c}</b>, count: x.quantity, lux: f.lux(x.lux), price: f.money(x.price_minor, x.currency), date: f.date(x.ends_at) }}
                />
              </span>
              <Switch checked={x.auto_renew} disabled={!canBuy || renew.isPending} onChange={(v) => renew.mutate({ id: x.id, auto_renew: v })} label={t("lux.luxSeats.renew")} />
            </li>
          ))}
        </ul>
      )}
      {confirming && q && (
        <Modal
          title={t("lux.luxSeats.confirmTitle")}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>{t("lux.spend.cancel")}</Button>
              <Button variant="primary" loading={buy.busy} onClick={() => buy.run(undefined)}>
                {t("lux.luxSeats.pay", { lux: f.lux(q.lux) })}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p style={{ margin: 0 }}>
              <FormattedMessage
                id="lux.luxSeats.confirmBody"
                values={{ b: (c) => <b>{c}</b>, count: n, term, date: f.date(q.ends_at), quote: seatQuoteText(q, f) }}
              />
            </p>
            <Switch checked={autoRenew} disabled={buy.busy} onChange={setAutoRenew} label={t("lux.luxSeats.autoRenew")} />
            <p className="muted small" style={{ margin: 0 }}>
              {t("lux.luxSeats.terms")}
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
